import { useParams, Link, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CityAutocomplete } from '@/components/common/CityAutocomplete';
import { useCandidateWithDetails, useCreateCandidate, useUpdateCandidate } from '../hooks';
import { useEffect, useState } from 'react';
import type { CitySearchResult } from '@/services/geography.service';
import { useDepartements, useRegions, useCountries } from '@/hooks/useGeography';

/**
 * Zod schema for optional number fields from HTML inputs.
 * Empty string or undefined → undefined, otherwise coerce to number.
 */
const optionalNumber = (min?: number, max?: number) =>
  z.preprocess(
    (val) => (val === '' || val === undefined || val === null ? undefined : val),
    (() => {
      let schema = z.coerce.number();
      if (min !== undefined) schema = schema.min(min);
      if (max !== undefined) schema = schema.max(max);
      return schema.optional();
    })()
  );

const candidateSchema = z.object({
  firstName: z.string().min(1, 'Le prénom est requis').max(150),
  lastName: z.string().min(1, 'Le nom est requis').max(150),
  email: z.string().email('Email invalide').optional().or(z.literal('')),
  phone: z.string().max(30).optional().or(z.literal('')),
  linkedinUrl: z.string().url('URL invalide').optional().or(z.literal('')),
  currentTitle: z.string().max(255).optional().or(z.literal('')),
  currentCompany: z.string().max(255).optional().or(z.literal('')),
  seniority: z.preprocess((v) => (v === '' ? undefined : v), z.enum(['junior', 'mid', 'senior', 'expert', 'lead']).optional()),
  yearsExperience: optionalNumber(0, 50),
  salaryExpectation: optionalNumber(0),
  contractPreference: z.enum(['cdi', 'freelance', 'both']).optional(),
  tjmExpectation: optionalNumber(0),
  availability: z.preprocess((v) => (v === '' ? undefined : v), z.enum(['available', 'open', 'not_looking']).optional()),
  locationCity: z.string().max(150).optional().or(z.literal('')),
  locationCountry: z.string().max(2).optional().or(z.literal('')),
  cityId: z.number().optional().nullable(),
  departementId: z.number().optional().nullable(),
  regionId: z.number().optional().nullable(),
  countryId: z.number().optional().nullable(),
  mobility: z.string().max(255).optional().or(z.literal('')),
  summary: z.string().optional().or(z.literal('')),
  source: z.string().max(100).optional().or(z.literal('')),
  status: z.enum(['active', 'archived', 'blacklisted']).optional(),
  rating: optionalNumber(0, 5),
});

type CandidateFormData = z.infer<typeof candidateSchema>;

const seniorityOptions = [
  { value: 'junior', label: 'Junior' },
  { value: 'mid', label: 'Confirmé' },
  { value: 'senior', label: 'Senior' },
  { value: 'expert', label: 'Expert' },
  { value: 'lead', label: 'Lead' },
];

const availabilityOptions = [
  { value: 'available', label: 'Disponible immédiatement' },
  { value: 'open', label: 'Ouvert aux opportunités' },
  { value: 'not_looking', label: 'Non disponible' },
];

const statusOptions = [
  { value: 'active', label: 'Actif' },
  { value: 'archived', label: 'Archivé' },
  { value: 'blacklisted', label: 'Liste noire' },
];


export function CandidateFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const candidateId = id ? parseInt(id) : undefined;

  const [selectedCity, setSelectedCity] = useState<CitySearchResult | null>(null);

  // Geographic data for cascading selects
  const { data: countries = [] } = useCountries();
  const { data: regions = [] } = useRegions();
  const { data: departements = [] } = useDepartements();

  const { data: candidate, isLoading: isLoadingCandidate } = useCandidateWithDetails(candidateId);
  const createCandidate = useCreateCandidate();
  const updateCandidate = useUpdateCandidate();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<CandidateFormData>({
    resolver: zodResolver(candidateSchema),
    defaultValues: {
      status: 'active',
      locationCountry: 'FR',
    },
  });

  useEffect(() => {
    if (candidate) {
      reset({
        firstName: candidate.firstName,
        lastName: candidate.lastName,
        email: candidate.email || '',
        phone: candidate.phone?.replace(/\s/g, '') || '',
        linkedinUrl: candidate.linkedinUrl || '',
        currentTitle: candidate.currentTitle || '',
        currentCompany: candidate.currentCompany || '',
        seniority: candidate.seniority || undefined,
        yearsExperience: candidate.yearsExperience || undefined,
        salaryExpectation: candidate.salaryExpectation || undefined,
        contractPreference: candidate.contractPreference as 'cdi' | 'freelance' | 'both' | undefined || undefined,
        tjmExpectation: candidate.tjmExpectation || undefined,
        availability: candidate.availability || undefined,
        locationCity: candidate.locationCity || '',
        locationCountry: candidate.locationCountry || '',
        cityId: candidate.cityId || null,
        departementId: candidate.departementId || null,
        regionId: candidate.regionId || null,
        countryId: candidate.countryId || null,
        mobility: candidate.mobility || '',
        summary: candidate.summary || '',
        source: candidate.source || '',
        status: candidate.status,
        rating: candidate.rating || undefined,
      });
    }
  }, [candidate, reset]);

  const onSubmit = async (data: CandidateFormData) => {
    // Clean empty strings to undefined
    const cleanedData: CandidateFormData = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email || undefined,
      phone: data.phone || undefined,
      linkedinUrl: data.linkedinUrl || undefined,
      currentTitle: data.currentTitle || undefined,
      currentCompany: data.currentCompany || undefined,
      seniority: data.seniority || undefined,
      yearsExperience: data.yearsExperience,
      salaryExpectation: data.salaryExpectation,
      contractPreference: data.contractPreference || undefined,
      tjmExpectation: data.tjmExpectation,
      availability: data.availability || undefined,
      locationCity: data.locationCity || undefined,
      locationCountry: data.locationCountry || undefined,
      cityId: data.cityId ?? undefined,
      departementId: data.departementId ?? undefined,
      regionId: data.regionId ?? undefined,
      countryId: data.countryId ?? undefined,
      mobility: data.mobility || undefined,
      summary: data.summary || undefined,
      source: data.source || undefined,
      status: data.status,
      rating: data.rating,
    };

    if (isEdit && candidateId) {
      await updateCandidate.mutateAsync({ id: candidateId, dto: cleanedData });
      navigate(`/itc/candidates/${candidateId}`);
    } else {
      await createCandidate.mutateAsync(cleanedData);
      navigate('/itc/candidates');
    }
  };

  if (isEdit && isLoadingCandidate) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Link to={isEdit ? `/itc/candidates/${candidateId}` : '/itc/candidates'}>
          <Button variant="ghost" size="sm">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour
          </Button>
        </Link>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{isEdit ? 'Modifier le candidat' : 'Nouveau candidat'}</CardTitle>
          <CardDescription>
            {isEdit ? 'Modifiez les informations du candidat' : 'Renseignez les informations du candidat'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            {/* Informations personnelles */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Informations personnelles</h3>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="firstName">Prénom *</Label>
                  <Input id="firstName" {...register('firstName')} />
                  {errors.firstName && (
                    <p className="text-sm text-destructive">{errors.firstName.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Nom *</Label>
                  <Input id="lastName" {...register('lastName')} />
                  {errors.lastName && (
                    <p className="text-sm text-destructive">{errors.lastName.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" {...register('email')} />
                  {errors.email && (
                    <p className="text-sm text-destructive">{errors.email.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Téléphone</Label>
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field }) => (
                      <PhoneInput
                        value={field.value}
                        onChange={field.onChange}
                        defaultCountry="FR"
                      />
                    )}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="linkedinUrl">LinkedIn</Label>
                  <Input id="linkedinUrl" {...register('linkedinUrl')} placeholder="https://linkedin.com/in/..." />
                  {errors.linkedinUrl && (
                    <p className="text-sm text-destructive">{errors.linkedinUrl.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Situation professionnelle */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Situation professionnelle</h3>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="currentTitle">Poste actuel</Label>
                  <Input id="currentTitle" {...register('currentTitle')} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="currentCompany">Entreprise actuelle</Label>
                  <Input id="currentCompany" {...register('currentCompany')} />
                </div>
                <div className="space-y-2">
                  <Label>Séniorité</Label>
                  <Select
                    value={watch('seniority') || ''}
                    onValueChange={(value) => setValue('seniority', value as CandidateFormData['seniority'])}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner..." />
                    </SelectTrigger>
                    <SelectContent>
                      {seniorityOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="yearsExperience">Années d'expérience</Label>
                  <Input id="yearsExperience" type="number" min="0" max="50" {...register('yearsExperience')} />
                  {errors.yearsExperience && (
                    <p className="text-sm text-destructive">{errors.yearsExperience.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Rémunération */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Rémunération</h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Préférence contractuelle</Label>
                  <div className="flex gap-2">
                    {[
                      { value: 'cdi', label: 'CDI' },
                      { value: 'freelance', label: 'Freelance' },
                      { value: 'both', label: 'Les deux' },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setValue('contractPreference', opt.value as 'cdi' | 'freelance' | 'both')}
                        className={`px-4 py-2 rounded-md border text-sm font-medium transition-colors ${
                          watch('contractPreference') === opt.value
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-background hover:bg-muted border-input'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {(watch('contractPreference') === 'cdi' || watch('contractPreference') === 'both' || !watch('contractPreference')) && (
                    <div className="space-y-2">
                      <Label htmlFor="salaryExpectation">Prétention salariale (€/an)</Label>
                      <Input id="salaryExpectation" type="number" min="0" {...register('salaryExpectation')} />
                      {errors.salaryExpectation && (
                        <p className="text-sm text-destructive">{errors.salaryExpectation.message}</p>
                      )}
                    </div>
                  )}
                  {(watch('contractPreference') === 'freelance' || watch('contractPreference') === 'both') && (
                    <div className="space-y-2">
                      <Label htmlFor="tjmExpectation">TJM (€/jour)</Label>
                      <Input id="tjmExpectation" type="number" min="0" {...register('tjmExpectation')} />
                      {errors.tjmExpectation && (
                        <p className="text-sm text-destructive">{errors.tjmExpectation.message}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Disponibilité et mobilité */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Disponibilité et mobilité</h3>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Disponibilité</Label>
                  <Select
                    value={watch('availability') || ''}
                    onValueChange={(value) => setValue('availability', value as CandidateFormData['availability'])}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availabilityOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mobility">Mobilité</Label>
                  <Input id="mobility" {...register('mobility')} placeholder="Ex: Île-de-France, Remote..." />
                </div>
              </div>
            </div>

            {/* Localisation — 4 champs en cascade */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Localisation</h3>
              <div className="grid gap-4 md:grid-cols-2">
                {/* Ville */}
                <div className="space-y-2">
                  <Label>Ville</Label>
                  <CityAutocomplete
                    value={selectedCity}
                    onSelect={(city) => {
                      setSelectedCity(city);
                      if (city) {
                        setValue('locationCity', city.name);
                        setValue('cityId', city.id);
                        setValue('departementId', city.departementId);
                        setValue('regionId', city.regionId ?? null);
                        setValue('countryId', city.countryId);
                        setValue('locationCountry', 'FR');
                      } else {
                        setValue('locationCity', '');
                        setValue('cityId', null);
                        // Ne pas effacer les niveaux supérieurs
                      }
                    }}
                  />
                </div>

                {/* Département */}
                <div className="space-y-2">
                  <Label>Département</Label>
                  <Select
                    key={`dept-${departements.length}-${watch('departementId')}`}
                    value={watch('departementId')?.toString() || ''}
                    onValueChange={(value) => {
                      const deptId = parseInt(value);
                      const dept = departements.find((d) => d.id === deptId);
                      setValue('departementId', deptId);
                      // Clear city (more specific)
                      setValue('locationCity', '');
                      setValue('cityId', null);
                      setSelectedCity(null);
                      // Auto-fill region + country
                      if (dept) {
                        setValue('regionId', dept.regionId ?? null);
                        setValue('countryId', dept.countryId);
                        const country = countries.find((c) => c.id === dept.countryId);
                        setValue('locationCountry', country?.alpha2 || 'FR');
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner..." />
                    </SelectTrigger>
                    <SelectContent>
                      {departements.map((d) => (
                        <SelectItem key={d.id} value={d.id.toString()}>
                          {d.code} - {d.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Région */}
                <div className="space-y-2">
                  <Label>Région</Label>
                  <Select
                    key={`reg-${regions.length}-${watch('regionId')}`}
                    value={watch('regionId')?.toString() || ''}
                    onValueChange={(value) => {
                      const regId = parseInt(value);
                      const reg = regions.find((r) => r.id === regId);
                      setValue('regionId', regId);
                      // Clear city + département (more specific)
                      setValue('locationCity', '');
                      setValue('cityId', null);
                      setValue('departementId', null);
                      setSelectedCity(null);
                      // Auto-fill country
                      if (reg) {
                        setValue('countryId', reg.countryId);
                        const country = countries.find((c) => c.id === reg.countryId);
                        setValue('locationCountry', country?.alpha2 || 'FR');
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner..." />
                    </SelectTrigger>
                    <SelectContent>
                      {regions.map((r) => (
                        <SelectItem key={r.id} value={r.id.toString()}>
                          {r.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Pays */}
                <div className="space-y-2">
                  <Label>Pays</Label>
                  <Select
                    key={`country-${countries.length}-${watch('countryId')}`}
                    value={watch('countryId')?.toString() || ''}
                    onValueChange={(value) => {
                      const cId = parseInt(value);
                      const country = countries.find((c) => c.id === cId);
                      setValue('countryId', cId);
                      setValue('locationCountry', country?.alpha2 || '');
                      // Clear city + département + région (more specific)
                      setValue('locationCity', '');
                      setValue('cityId', null);
                      setValue('departementId', null);
                      setValue('regionId', null);
                      setSelectedCity(null);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner..." />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((c) => (
                        <SelectItem key={c.id} value={c.id.toString()}>
                          {c.nameFr}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Résumé */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Résumé</h3>
              <div className="space-y-2">
                <Label htmlFor="summary">Résumé du profil</Label>
                <Textarea
                  id="summary"
                  {...register('summary')}
                  rows={4}
                  placeholder="Décrivez brièvement le profil du candidat..."
                />
              </div>
            </div>

            {/* Métadonnées */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Informations additionnelles</h3>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="source">Source</Label>
                  <Input id="source" {...register('source')} placeholder="Ex: LinkedIn, Cooptation..." />
                </div>
                {isEdit && (
                  <>
                    <div className="space-y-2">
                      <Label>Statut</Label>
                      <Select
                        value={watch('status') || 'active'}
                        onValueChange={(value) => setValue('status', value as CandidateFormData['status'])}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {statusOptions.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="rating">Note (0-5)</Label>
                      <Input id="rating" type="number" min="0" max="5" step="0.5" {...register('rating')} />
                      {errors.rating && (
                        <p className="text-sm text-destructive">{errors.rating.message}</p>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Erreurs globales */}
            {Object.keys(errors).length > 0 && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3">
                <p className="text-sm text-destructive">
                  Veuillez corriger les erreurs dans le formulaire avant d'enregistrer.
                </p>
                <ul className="mt-2 text-xs text-destructive">
                  {Object.entries(errors).map(([field, err]) => (
                    <li key={field}>{field}: {(err as any)?.message || 'invalide'}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-4">
              <Link to={isEdit ? `/itc/candidates/${candidateId}` : '/itc/candidates'}>
                <Button variant="outline" type="button">
                  Annuler
                </Button>
              </Link>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    {isEdit ? 'Enregistrer' : 'Créer le candidat'}
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
